import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Lock } from 'lucide-react';
import { Field, FilePicker, inputCls } from '../shared/FormKit';
import { FieldInvalidInline } from '../shared/formValidation';
import GroupedEngagementPicker from '../workflow/GroupedEngagementPicker';
import EditableAttachments from '../workflow/EditableAttachments';
import { CoveredEngagementsPanel, EngagementNameList, CoverageCell } from '../workflow/CoverageDisplay';
import NowName from '../shared/NowName';
import CompanyList from '../workflow/CompanyList';
import { engagementNo } from '../../utils/engagementNo';
import {
  qcAnswerGaps, qcSplitGaps, legacyAnswerGaps, feeAnswersChanged, qcRiskRatingChangedOf, QC_TYPE_LABEL,
} from '../../pages/qcQuestions';
import { QcAnswersForm } from '../../pages/WorkflowQcNew';
import { QcLegacyForm } from '../workflow/QcForm';
import { ImportModeSwitch } from './legacyImport';
import { formatDate, formatDateTime, formatDateShort } from '../../utils/dateFormat';
import { fetchJSON, API_BASE_URL } from '../../utils/api';

// ── QC6/QC7 record — the ONE field set ───────────────────────────────────────
//
// recordKinds.js（fs/qc35）的同一套「一份設定餵所有畫面」模式：清單欄位、詳情
// 閱讀列、Source modal 欄位與建立/編輯表單的欄位定義只有這裡有，清單頁
// （QMQcRecords）、詳情頁（QMQcRecordDetail）與 New 頁（QMQcRecordNew）都吃這份。
// 沒收成一份之前，清單欄位與詳情列各自手寫、Source modal 的欄位在兩頁一字不差
// 抄兩份——同一個欄位改動要改四處，遲早有一份漏掉。
//
// **刻意不併進 RECORD_KINDS**：那張表就是 `/qm/records/:kind` 的路由白名單，
// 塞進 `qc` 會讓 `/qm/records/qc` 變成活路由、還打錯 API。形狀也不同——qc 的
// 清單欄位帶 sortKey、Source 欄要開 modal（吃頁面的 callback）、詳情列要讀
// usedByOutside——硬套 fs/qc35 的簽名只會兩邊都彆扭。所以是**同模式的兄弟檔**：
//
//   columns(onOpenSource)   清單的欄位（Source 徽章開 modal，callback 由頁面給）
//   rows(record, usedByOutside)  詳情的閱讀列（[label, node] 對，同 fs/qc35 的 rows）
//   sourceFields(r)         Source modal 的欄位（兩頁共用同一份）
//
// 表單同 recordKinds 的理由不做宣告式 DSL：新增是分步頁（QMQcRecordNew，形狀
// ＝WorkflowQcNew）、編輯是 modal（RecordFormModal，形狀＝QcModifyModal），
// 外殼不同、欄位是下面同一組區塊元件，內容不可能漂移。
//
// 三件對齊 workflow 的事都落在這裡：
//   · 先挑 engagement（GroupedEngagementPicker，與 QC New/Modify 同一支）
//   · FYE 唯讀——它是 engagement 的欄位，picker 面板已經寫著，編輯時再單獨
//     標一次並上鎖圖示
//   · 沒有 submission method；簽署文件只在 legacy import 模式上傳
//
// Legacy import（開關開啟時步驟 3 多一個「Upload signed form」）：記錄仍是普通的
// form 記錄，只是 answers 只有六題下游欄位（QcLegacyForm）、多一份簽署 PDF
// （signedForm → QC_FORM）、`legacyImport=true`。編輯時只改那六題、可換 PDF；
// 補齊整張表走 QC Amendment。

export const fmtAnswer = (q, v) => {
  if (q.type === 'checkbox') return v === true ? 'Confirmed' : 'Not confirmed';
  return (v === undefined || v === null || String(v).trim() === '') ? '—' : String(v);
};

export const SourceBadge = ({ record, onOpen }) => (
  record.sourceRequestNo ? (
    <button
      type="button"
      onClick={onOpen}
      title="View the application that produced this record"
      className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700 hover:bg-blue-100"
    >
      {record.sourceRequestNo}
    </button>
  ) : null
);

export const QC_RECORD_KIND = {
  key: 'qc',
  label: 'QC6 / QC7',

  columns: (onOpenSource) => [
    {
      key: 'qcType',
      label: 'Type',
      sortKey: 'qcType',
      render: (r) => (
        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-50 text-blue-700">{r.qcType}</span>
      ),
    },
    // The record's frozen group name; "(now: …)" when the client master moved on.
    { key: 'clientGroup', label: 'Client Group', sortKey: 'clientGroup', render: (r) => <NowName value={r.clientGroup} now={r.clientGroupNow} className="text-slate-600" /> },
    // 涵蓋的案（順序同申請單），每案自帶公司／UEN／FYE——同 FS／QC35 清單。
    { key: 'coverage', label: 'Engagement(s)', render: (r, ctx) => <CoverageCell record={r} ctx={ctx} /> },
    { key: 'approvedOn', label: 'Approval Date', sortKey: 'approvedOn', render: (r) => <span className="text-slate-600">{r.approvedOn ? formatDateShort(r.approvedOn) : '—'}</span> },
  ],

  rows: (record, usedByOutside = []) => [
    [
      (record.entities || []).length > 1
        ? `Companies Covered (${record.entities.length})`
        : 'Entity',
      (record.entities || []).length > 0 ? (
        <CompanyList entities={record.entities} />
      ) : (
        <NowName
          value={`${record.entityName}${record.uen ? ` (${record.uen})` : ''}`}
          now={(record.entityNameNow || record.uenNow) ? `${record.entityNameNow || record.entityName} (${record.uenNow || record.uen})` : null}
        />
      ),
    ],
    ['Client Group', <NowName value={record.clientGroup} now={record.clientGroupNow} />],
    // Same row as the QC7 request page, read off the form's answers.
    ...(record.qcType === 'QC7' && record.answers ? [['Risk Rating Changed from Prior Year', (() => {
      const changed = qcRiskRatingChangedOf(record.answers, 'QC7');
      return changed == null ? '—' : (changed ? 'Yes (from the form)' : 'No (from the form)');
    })()]] : []),
    ['Approval Date', record.approvedOn ? formatDate(record.approvedOn) : '—'],
    // 這一格只回答「簽署檔是哪一份」——記錄怎麼來的由頂端說明列講，問卷為何
    // 大半空白由下面的表單卡講,一句話不兼三個差事。
    ...(record.legacyImport ? [['Signed Form', record.signedFormPending
      ? <span className="text-slate-500">Not uploaded yet</span>
      : ((record.files || []).find((f) => f.docType === 'QC_FORM' && !f.superseded)?.originalName || 'Uploaded')]] : []),
    // 涵蓋的案子＝這筆 QC 蓋章的對象（qc_record_engagements）。
    [
      (record.engagements || []).length > 1
        ? `Engagements Covered (${record.engagements.length})`
        : 'Engagement Covered',
      (record.engagements || []).length === 0 ? '—' : <CoveredEngagementsPanel items={record.engagements} />,
    ],
    ['Remarks', record.remarks || '—'],
    // 連結只從 QC 這端產生，正常時引用＝涵蓋；只有涵蓋外的引用（舊資料）才列，
    // 它也是刪除被擋的原因。
    ...(usedByOutside.length > 0 ? [[
      'Linked Outside Coverage',
      <EngagementNameList items={usedByOutside} noOf={(e) => engagementNo(e.id)} keyOf={(e, i) => e.id || i} />,
    ]] : []),
  ],

  sourceFields: (r) => [
    ['Request', r.requestNo],
    ['Status', r.status],
    ['Type', r.qcType],
    // 每案自帶公司／UEN／FYE（同審核 Detail 的 Engagement(s) Covered）。
    [`Engagement(s) Covered (${(r.engagements || []).length})`, (r.engagements || []).length > 0
      ? <EngagementNameList items={r.engagements} keyOf={(e, i) => e.id || i} />
      : `${r.entityName}${r.uen ? ` (${r.uen})` : ''}`],
    ['Requested By', r.requesterEmail],
    ['Submitted', formatDateTime(r.createdAt)],
  ],
};

// ── 表單 ─────────────────────────────────────────────────────────────────────

/** 表單初值：新增給空的，編輯從記錄帶。 */
export const initialQcRecordForm = (record) => ({
  qcType: record ? record.qcType : '',
  approvedOn: record ? (record.approvedOn || '') : '',
  engagementIds: record
    ? (record.engagements || []).map((e) => e.engagementId).filter(Boolean)
    : [],
  answers: record ? (record.answers || {}) : {},
  remarks: record ? (record.remarks || '') : '',
  files: [],
  // Legacy import: decided on create, read-only afterwards; `signedForm` is
  // the PDF to attach (create: required; edit: replaces the current one).
  legacyImport: record ? record.legacyImport === true : false,
  signedForm: [],
});

/**
 * 挑選器的候選清單。`recordId` 讓後端把這筆記錄自己的涵蓋案放回來——建立時就
 * 回寫了 qc_record_id，不放回來的話編輯畫面會看不到自己涵蓋的案子。
 */
export const useQcRecordOptions = (recordId = null) => {
  const [engagements, setEngagements] = useState([]);
  const [ownIds, setOwnIds] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const qs = recordId ? `?recordId=${recordId}` : '';
        const data = await fetchJSON(`/api/qm/qc-records/options${qs}`);
        if (cancelled) return;
        setEngagements(data.engagements || []);
        setOwnIds(data.ownEngagementIds || []);
      } catch (e) { if (!cancelled) setError(e.message); }
    })();
    return () => { cancelled = true; };
  }, [recordId]);

  return { engagements, ownIds, optionsError: error };
};

/**
 * 送出前的必填檢查，兩個外殼共用一份 → `[{ key, label }]`。key＝那一格控制項
 * 的 `field=`（問卷逐題是 `answers.<id>`，QcForm 自己標），頁面交給
 * formValidation 就地標紅；label＝橫幅用語。
 */
export const qcRecordFormGaps = (form, { record = null, allowMissingSignedForm = false } = {}) => {
  const gaps = [];
  if (!form.qcType) gaps.push({ key: 'qcType', label: 'QC Type' });
  if ((form.engagementIds || []).length === 0) gaps.push({ key: 'engagementIds', label: 'Engagement (At Least One)' });
  if (!form.approvedOn) gaps.push({ key: 'approvedOn', label: 'Approval Date' });
  if (form.legacyImport) {
    // Imported record: the six downstream answers + the signed PDF — unless
    // Admin Settings allow creating the record before the form is at hand
    // ("Signed form pending"). On edit the PDF is never required: the record
    // either has one or is waiting for its first upload.
    const hasSigned = (record?.files || []).some((f) => f.docType === 'QC_FORM' && !f.superseded);
    if ((form.signedForm || []).length === 0 && !hasSigned && !record && !allowMissingSignedForm) {
      gaps.push({ key: 'signedForm', label: 'Signed QC6 / QC7 Form (PDF)' });
    }
    if (form.qcType) gaps.push(...legacyAnswerGaps(form.answers, form.qcType));
  } else if (form.qcType) {
    gaps.push(...qcAnswerGaps(form.answers, form.qcType));
  }
  // Every engagement's own fee: a new record, or an existing one whose fee this edit changes.
  const requireFee = !form.legacyImport && (!record || feeAnswersChanged(record.answers, form.answers, form.qcType));
  if (form.qcType) gaps.push(...qcSplitGaps(form.answers, form.qcType, form.engagementIds, { requireFee }));
  return gaps;
};

/** FormData 組裝，同上——一份。FYE 與 submissionMethod 刻意不送（後端推導）。 */
export const buildQcRecordBody = (form, { removeFileIds = [] } = {}) => {
  const fd = new FormData();
  fd.set('qcType', form.qcType);
  fd.set('approvedOn', form.approvedOn || '');
  fd.set('engagementIds', JSON.stringify(form.engagementIds));
  fd.set('answers', JSON.stringify(form.answers));
  fd.set('remarks', form.remarks || '');
  fd.set('legacyImport', form.legacyImport ? 'true' : 'false');
  (form.files || []).forEach((f) => fd.append('files', f));
  (form.signedForm || []).slice(0, 1).forEach((f) => fd.append('signedForm', f));
  if (removeFileIds.length > 0) fd.set('removeFileIds', JSON.stringify(removeFileIds));
  return fd;
};

// ── 區塊 ─────────────────────────────────────────────────────────────────────

const QC_TYPE_CHOICES = [
  ['QC6', QC_TYPE_LABEL.QC6 || 'QC6 — new client acceptance'],
  ['QC7', QC_TYPE_LABEL.QC7 || 'QC7 — existing client continuance'],
];

/**
 * 型別是這筆記錄的身分。鎖成唯讀晶片（同 QC Modify 的作法）的兩種情形：核准
 * 產生的記錄（更正走 QCA）、或被涵蓋清單以外的案子引用。
 */
export const QcTypeSection = ({ value, onChange, locked = false, lockReason = null }) => {
  if (locked) {
    const label = (QC_TYPE_CHOICES.find(([v]) => v === value) || [])[1] || '';
    const description = label.split('—').slice(1).join('—').trim();
    return (
      <Field label="QC Type">
        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
          <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          <span className="text-sm font-semibold text-slate-900">{value}</span>
          {description && <span className="text-sm text-slate-500">{description}</span>}
        </div>
        {lockReason && <p className="text-xs text-slate-500 mt-1">{lockReason}</p>}
      </Field>
    );
  }
  return (
    <Field label="QC Type" required field="qcType">
      <div className="flex flex-wrap gap-4">
        {QC_TYPE_CHOICES.map(([v, label]) => (
          <label key={v} className="flex items-center gap-2 text-sm text-slate-700">
            <input type="radio" name="qcRecordType" value={v} checked={value === v} onChange={() => onChange(v)} />
            {label}
          </label>
        ))}
      </div>
    </Field>
  );
};

/**
 * 核准日（0147）。這份表單在系統外早就簽核完成，管理者把日期登記進來；
 * workflow 產生的記錄由核准自動寫，這格唯讀。
 */
export const QcApprovalDateSection = ({ value, onChange, locked = false }) => (
  <Field label="Approval Date" required={!locked} field={locked ? null : 'approvedOn'} hint={locked
    ? 'Set by the approval that produced this record — it cannot be changed.'
    : 'The date the signed QC6 / QC7 was approved — printed on the PDF and used by any addendum.'}>
    {locked ? (
      <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
        <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <span className="text-sm font-semibold text-slate-900">{value ? formatDate(value) : '—'}</span>
      </div>
    ) : (
      <input type="date" value={value || ''} onChange={(e) => onChange(e.target.value)} className={`${inputCls} max-w-xs`} />
    )}
  </Field>
);

/**
 * 涵蓋的 engagement。`lockedIds` = 這筆記錄已經蓋章的案子——**可以移除**
 * （與 FS/QC35 記錄頁一致）：拿掉的案在存檔時解除
 * 連結並留歷程，該案的 Partner 收知會信。仍傳進來是為了畫警示，不再鎖勾選。
 * `fallbackYearEnd` 供編輯時顯示——涵蓋案被刪掉時 picker 面板沒東西可讀，
 * 但記錄自己的 FYE 還在。
 */
export const QcCoverageSection = ({
  engagements, selectedIds, onChange, lockedIds = [], fallbackYearEnd = null, isEdit = false,
}) => {
  const primary = useMemo(
    () => engagements.find((e) => e.id === selectedIds[0]) || null,
    [engagements, selectedIds]
  );
  const yearEnd = primary ? primary.yearEnd : fallbackYearEnd;
  const dropping = lockedIds.filter((id) => !selectedIds.includes(id));
  return (
    <>
      <GroupedEngagementPicker
        engagements={engagements}
        selectedIds={selectedIds}
        onChange={onChange}
      />
      {/* 挑選器不是 Field——「Required」就標在它正下方。 */}
      <FieldInvalidInline field="engagementIds" text="Required — pick at least one engagement" />
      {dropping.length > 0 && (
        <p className="text-xs text-red-700">
          {dropping.length === 1 ? 'One covered engagement' : `${dropping.length} covered engagements`} will be
          un-linked from this record on save — its qc type clears and its partner is notified.
        </p>
      )}
      {/* FYE 不允許修改。它跟著第一個涵蓋案走（同組公司只要同年份，日期可不同），
          所以這裡只給值＋鎖頭。 */}
      <Field label="Financial Year End">
        <div className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-slate-50">
          <Lock className="w-3.5 h-3.5 text-slate-400 shrink-0" title="Comes from the engagement" />
          <span className="text-sm font-semibold text-slate-900">{yearEnd ? formatDate(yearEnd) : '—'}</span>
          <span className="text-sm text-slate-500">
            {yearEnd ? "from the engagement" : (isEdit ? 'no covered engagement' : 'pick an engagement first')}
          </span>
        </div>
      </Field>
    </>
  );
};

/**
 * 問卷＝這筆記錄本身。`engagements`＝涵蓋案（slim-list 形狀，第一個是
 * primary）——表頭從它讀；填法與 workflow 是同一份表單。
 *
 * Legacy import：`legacyEnabled`（開關）為 true 且是新建時，頂端多一個兩種填法
 * 的切換；`legacyImport` 模式只渲染六題（QcLegacyForm）＋簽署 PDF 上傳格。編輯
 * 既有記錄時模式鎖定（`record` 有值）——補齊整張表走 QC Amendment。
 */
export const QcAnswersSection = ({
  qcType, answers, onChange, engagements = [],
  legacyImport = false, onLegacyImportChange = null, legacyEnabled = false,
  signedForm = [], onSignedForm = null, record = null, allowMissingSignedForm = false,
}) => {
  const canSwitch = !record && legacyEnabled && onLegacyImportChange;
  const currentSigned = (record?.files || []).filter((f) => f.docType === 'QC_FORM' && !f.superseded);
  // Editing a record that never got its signed form: this upload is the
  // first version, not a replacement.
  const pendingUpload = !!record && currentSigned.length === 0;
  return (
    <>
      {canSwitch && <ImportModeSwitch value={legacyImport} onChange={onLegacyImportChange} />}
      {legacyImport && (
        <Field
          label={pendingUpload ? 'Upload the Signed Form' : record ? 'Replace the Signed Form' : 'Signed QC6 / QC7 Form'}
          required={!record && !allowMissingSignedForm}
          field="signedForm"
          hint={pendingUpload
            ? 'This record was imported without its signed form. Attach the signed PDF here — it becomes the first version on file.'
            : record
              ? 'Uploading replaces the current signed form — the previous version stays on the record.'
              : allowMissingSignedForm
                ? 'Signed and approved form, PDF. Can be uploaded later from Edit.'
                : 'Signed and approved form, PDF.'}
        >
          {currentSigned.length > 0 && (
            <p className="text-xs text-slate-500 mb-1">Current: {currentSigned.map((f) => f.originalName).join(', ')}</p>
          )}
          <FilePicker
            kind="pdfOnly"
            onChange={(e) => onSignedForm && onSignedForm(Array.from(e.target.files || []).slice(0, 1))}
            files={signedForm || []}
          />
        </Field>
      )}
      {!qcType ? (
        <p className="text-sm text-slate-500">Select the QC type above to load the QC6 / QC7 form.</p>
      ) : legacyImport ? (
        // Editing an existing record: the rating, its reason and the EQR are
        // addendum-only (locked rows say so); a NEW record fills them here.
        <QcLegacyForm qcType={qcType} answers={answers} onChange={onChange} engagements={engagements} addendumOnly={record ? (record.answers || {}) : false} />
      ) : (
        <QcAnswersForm
          qcType={qcType}
          answers={answers}
          onChange={onChange}
          engagements={engagements}
          addendumOnly={record ? (record.answers || {}) : false}
          feePerEngagement={!record || feeAnswersChanged(record.answers, answers, qcType)}
        />
      )}
    </>
  );
};

/** 附件：新增只有上傳格,編輯多一份現有清單（workflow Modify 的同一支元件）。 */
export const QcAttachmentsSection = ({
  files, onFiles, remarks, onRemarks, record = null, removeIds = [], onToggleRemove = null,
}) => (
  <>
    {record && (record.files || []).length > 0 && onToggleRemove && (
      <EditableAttachments
        // 匯入記錄的現行簽署表單只能換版（上面的「Replace the signed form」），
        // 不給移除鈕——後端也擋（409 SIGNED_FORM_LOCKED）。
        files={record.files.filter((f) => !f.superseded && !(record.legacyImport && f.docType === 'QC_FORM'))}
        removeIds={removeIds}
        onToggle={onToggleRemove}
        downloadHref={(f) => `${API_BASE_URL}/api/qm/qc-records/${record.id}/files/${f.id}`}
        label="Current Attachments"
        hint={record.legacyImport
          ? 'Superseded versions are kept on the record and are not shown here. The signed form is replaced above, never removed.'
          : 'Superseded versions are kept on the record and are not shown here.'}
      />
    )}
    <Field label="Supporting Files" hint="AML screenshots, predecessor clearance, other evidence. PDF / DOC / DOCX / JPG / PNG.">
      <FilePicker
        multiple kind="docsAndImages"
        onChange={(e) => onFiles(Array.from(e.target.files || []))}
        files={files || []}
      />
    </Field>
    <Field label="Remarks">
      <textarea rows="2" value={remarks} onChange={(e) => onRemarks(e.target.value)} className={inputCls} />
    </Field>
  </>
);

/**
 * 回寫結果 → 一句話。建立與編輯都會把涵蓋案的 qc_type / qc_record_id 蓋上去,
 * 失敗要說出來（engagementRecordApply 的原則:核准側只 log,管理側要講）。
 */
export const applyNotice = (applied) => {
  if (!applied) return null;
  const wrote = (applied.written || []).length;
  const failed = applied.failed || [];
  if (failed.length > 0) {
    return `Record saved, but the QC link could not be written to ${failed.length} engagement(s) — open them and check.`;
  }
  if (wrote > 0) {
    return `Record saved — ${wrote} engagement${wrote === 1 ? '' : 's'} now linked to this QC.`;
  }
  return null;
};

/** 建立/編輯共用的存檔（modal 與頁面都呼叫這支）。 */
export const saveQcRecord = async (form, { record = null, removeFileIds = [] } = {}) => {
  const body = buildQcRecordBody(form, { removeFileIds });
  return record
    ? fetchJSON(`/api/qm/qc-records/${record.id}`, { method: 'PATCH', body })
    : fetchJSON('/api/qm/qc-records', { method: 'POST', body });
};

/** 讀一筆記錄的鎖定狀態（涵蓋案、清單外的引用）。 */
export const useQcRecordLocks = (recordId) => {
  const [locks, setLocks] = useState({ coveredEngagementIds: [], usedByOutside: [] });
  const load = useCallback(async () => {
    if (!recordId) return;
    try {
      const data = await fetchJSON(`/api/qm/qc-records/${recordId}`);
      setLocks({
        coveredEngagementIds: data.coveredEngagementIds || [],
        usedByOutside: data.usedByOutside || [],
      });
    } catch { /* 鎖定只是提示——後端才是強制的那一道 */ }
  }, [recordId]);
  useEffect(() => { load(); }, [load]);
  return locks;
};
